import { useLocale } from '@/renderer/utils/locale';
import { isPreviewModeActive } from '@/renderer/utils/previewMode';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { useMemo } from 'react';
import { CFBIcon, NFLIcon } from './ProductIcons';
import {
  type SportSaleStatus,
  isOfferPurchasable,
  isProductPurchasable,
  sportSaleStatus,
} from './productHelpers';

type IconType = (props: React.ComponentPropsWithoutRef<'svg'>) => JSX.Element;

interface SportDef {
  sportId: number;
  name: string;
  route: string;
  Icon: IconType;
  tagline: string;
  /** static Tailwind gradient classes (must be literal for the compiler) */
  gradient: string;
}

// Order here controls display order on the landing page. College basketball is
// deliberately left out: the chooser offers no CBB option until that is decided
// on purpose, whatever the product data says.
const SPORTS: SportDef[] = [
  {
    sportId: 3,
    name: 'NFL',
    route: '/order/nfl',
    Icon: NFLIcon,
    tagline: 'Sides, totals & halves, preseason through the Super Bowl.',
    gradient: 'from-emerald-900 via-zinc-900 to-zinc-950',
  },
  {
    sportId: 1,
    name: 'College Football',
    route: '/order/cfb',
    Icon: CFBIcon,
    tagline: 'FBS & FCS sides and totals, all season long.',
    gradient: 'from-teal-800 via-emerald-950 to-zinc-950',
  },
];

const SportCard = ({
  sport,
  price,
  status,
}: {
  sport: SportDef;
  price: string | null;
  status: Exclude<SportSaleStatus, { kind: 'none' }>;
}) => {
  const { Icon } = sport;
  const onSale = status.kind === 'on-sale';
  return (
    <a
      href={sport.route}
      className="group relative isolate flex min-h-64 flex-col justify-between overflow-hidden rounded-2xl p-7 ring-1 ring-white/10 transition hover:ring-white/25 sm:min-h-72"
    >
      {/* Branded gradient background */}
      <div className={`absolute inset-0 z-0 bg-linear-to-br ${sport.gradient}`} />
      {/* Large icon watermark */}
      <Icon className="-bottom-8 -right-6 absolute z-0 size-52 fill-white/[0.06] transition-transform duration-500 group-hover:scale-105" />

      {/* Top: availability tag */}
      <div className="relative z-10">
        {onSale ? (
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-emerald-500/20 px-2.5 py-1 font-semibold text-emerald-300 text-xs ring-1 ring-emerald-400/40">
            <span className="size-1.5 rounded-full bg-emerald-400" />
            Available now
          </span>
        ) : (
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 font-semibold text-xs text-zinc-200 ring-1 ring-white/20">
            {status.copy.title}
          </span>
        )}
      </div>

      {/* Bottom: title + tagline + price */}
      <div className="relative z-10">
        <div className="flex items-center gap-3">
          <Icon className="size-8 shrink-0 fill-white" />
          <h2 className="font-bold text-2xl text-white leading-tight tracking-tight">
            {sport.name}
          </h2>
        </div>
        <p className="mt-2 max-w-xs text-sm text-zinc-300 leading-snug">{sport.tagline}</p>
        <div className="mt-5 flex items-center justify-between">
          {price && (
            <span className="text-sm text-zinc-400">
              From <span className="font-semibold text-white">{price}</span>
            </span>
          )}
          <span className="ml-auto inline-flex items-center gap-1 font-semibold text-sm text-white">
            {onSale ? 'View packages' : 'See details'}
            <ArrowRightIcon className="size-4 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </a>
  );
};

const SportCards = ({
  products,
}: {
  products: API.MembershipProduct[] | undefined;
  isLoading?: boolean;
}) => {
  const { locale } = useLocale();

  const cards = useMemo(() => {
    if (!products) {
      return [];
    }
    const preview = isPreviewModeActive();
    return SPORTS.map((sport) => {
      const sportProducts = products.filter((p) => p.sportIds?.includes(sport.sportId));
      // Same purchasable rule as the product page: a sport is "Available now" only
      // while something is on sale, else it shows its between-seasons state.
      const status = sportSaleStatus(sportProducts, { preview });
      if (status.kind === 'none') {
        return null;
      }

      const prices = sportProducts
        .filter((p) => isProductPurchasable(p, { preview }))
        .flatMap((p) => p.offers ?? [])
        .filter((o) => isOfferPurchasable(o))
        .map((o) => o.price)
        .filter((p): p is number => typeof p === 'number' && p > 0);
      const minPrice = prices.length ? Math.min(...prices) : null;
      const priceLabel =
        minPrice != null
          ? new Intl.NumberFormat(locale, {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            }).format(minPrice)
          : null;

      return { sport, price: status.kind === 'on-sale' ? priceLabel : null, status };
    }).filter(Boolean) as Array<{
      sport: SportDef;
      price: string | null;
      status: Exclude<SportSaleStatus, { kind: 'none' }>;
    }>;
  }, [products, locale]);

  // Still loading (no products yet) — show a skeleton so the empty state never flashes.
  if (!products) {
    return (
      <div className="py-8">
        <div className="mb-6">
          <div className="h-7 w-52 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
          <div className="mt-2 h-4 w-80 max-w-full animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="min-h-64 animate-pulse rounded-2xl bg-zinc-200 sm:min-h-72 dark:bg-zinc-800/50"
            />
          ))}
        </div>
      </div>
    );
  }

  if (cards.length === 0) {
    return null;
  }

  return (
    <div className="py-8">
      <div className="mb-6">
        <h1 className="font-bold text-2xl text-zinc-950 tracking-tight dark:text-white">
          Choose your sport
        </h1>
        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          Pick a sport to see this season's packages and pricing.
        </p>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        {cards.map(({ sport, price, status }) => (
          <SportCard key={sport.sportId} sport={sport} price={price} status={status} />
        ))}
      </div>
    </div>
  );
};

export default SportCards;
